import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseUrlFiltersOptions<T extends Record<string, unknown>> {
  defaults: T;
  numberFields?: (keyof T)[];
  booleanFields?: (keyof T)[];
}

export function useUrlFilters<T extends Record<string, unknown>>({
  defaults,
  numberFields = [],
  booleanFields = [],
}: UseUrlFiltersOptions<T>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const numberSet = useMemo(() => new Set(numberFields as string[]), [numberFields]);
  const booleanSet = useMemo(() => new Set(booleanFields as string[]), [booleanFields]);

  const filters = useMemo<T>(() => {
    const parsed = { ...defaults };

    for (const key of Object.keys(defaults)) {
      const raw = searchParams.get(key);
      if (raw === null) continue;

      if (numberSet.has(key)) {
        const num = Number(raw);
        if (!isNaN(num)) {
          (parsed as Record<string, unknown>)[key] = num;
        }
      } else if (booleanSet.has(key)) {
        (parsed as Record<string, unknown>)[key] = raw === 'true';
      } else {
        (parsed as Record<string, unknown>)[key] = raw;
      }
    }

    return parsed;
  }, [searchParams, defaults, numberSet, booleanSet]);

  const updateFilter = useCallback(
    <K extends keyof T>(key: K, value: T[K], resetPage = false) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (value === undefined || value === null || value === '') {
          newParams.delete(String(key));
        } else {
          newParams.set(String(key), String(value));
        }

        if (resetPage && key !== 'page') {
          newParams.set('page', '0');
        }

        return newParams;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const updateFilters = useCallback(
    (updates: Partial<T>, resetPage = true) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            newParams.delete(key);
          } else {
            newParams.set(key, String(value));
          }
        });

        if (resetPage) {
          newParams.set('page', '0');
        }

        return newParams;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const setFilters = useCallback((newFilters: T) => {
    setSearchParams(() => {
      const newParams = new URLSearchParams();

      Object.entries(newFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          newParams.set(key, String(value));
        }
      });

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const setPage = useCallback((page: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', String(page));
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const setLimit = useCallback((limit: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('limit', String(limit));
      newParams.set('page', '0');
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const handleSort = useCallback((field: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      const currentOrderBy = prev.get('orderBy');
      const currentOrder = prev.get('order');

      if (currentOrderBy !== field) {
        newParams.set('orderBy', field);
        newParams.set('order', 'asc');
      } else if (currentOrder === 'asc') {
        newParams.set('order', 'desc');
      } else if (currentOrder === 'desc') {
        newParams.delete('orderBy');
        newParams.delete('order');
      } else {
        newParams.set('order', 'asc');
      }

      newParams.set('page', '0');
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    updateFilter,
    updateFilters,
    setFilters,
    resetFilters,
    setPage,
    setLimit,
    handleSort,
  };
}
